import { useTheme, Box, Typography } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import moment from 'moment';
import { TimeLabel } from '../../label/TimeLabel';

const WeeklyEventBox = ({
  event,
  dateToPixel,
  day,
  isFirst,
  isLast,
  isFirstInWeek,
  isSelected,
  onClick,
  onEventEdit,
  onEventSelect,
  offsetTop = 0,
  selectedPeriod,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { label, color, startDateTime, endDateTime, category } = event;

  // Clamp event times to the visible day
  const start = moment.max(
    moment(startDateTime),
    moment(day.date).startOf('day')
  );
  const end = moment.min(moment(endDateTime), moment(day.date).endOf('day'));

  if (!start.isBefore(end)) return null;

  const startPos = dateToPixel(start);
  const endPos = dateToPixel(end);

  const baseColor = category?.color || color;
  const bgColor = isSelected ? colors.info + '40' : baseColor + '40';

  const isWholeDay = event.wholeDay === true;

  const calculateEventLayout = () => {
    const currentEventStart = moment(startDateTime);
    const currentEventEnd = moment(endDateTime);

    // Get all events for this day sorted by start time
    const allEvents = [...day.events].sort((a, b) =>
      moment(a.startDateTime).diff(moment(b.startDateTime))
    );

    // Find all events that overlap with current event
    const overlappingEvents = allEvents.filter((otherEvent) => {
      if (otherEvent.id === event.id) return true;

      const otherStart = moment(otherEvent.startDateTime);
      const otherEnd = moment(otherEvent.endDateTime);

      return (
        currentEventStart.isBefore(otherEnd) &&
        currentEventEnd.isAfter(otherStart)
      );
    });

    // Sort overlapping events by start time, then by duration (longer events first)
    const sortedOverlapping = overlappingEvents.sort((a, b) => {
      const startDiff = moment(a.startDateTime).diff(moment(b.startDateTime));
      if (startDiff !== 0) return startDiff;

      const aDuration = moment(a.endDateTime).diff(moment(a.startDateTime));
      const bDuration = moment(b.endDateTime).diff(moment(b.startDateTime));
      return bDuration - aDuration; // Longer events first
    });

    // Find events that start at exactly the same time as current event
    const sameStartTime = sortedOverlapping.filter((e) =>
      moment(e.startDateTime).isSame(currentEventStart)
    );

    // If more than 3 events start at same time, use original overlap logic
    if (sameStartTime.length > 3) {
      let overlapIndex = 0;

      for (const other of sortedOverlapping) {
        if (other.id === event.id) {
          break;
        }

        const otherStart = moment(other.startDateTime);
        const otherEnd = moment(other.endDateTime);

        if (
          currentEventStart.isBefore(otherEnd) &&
          currentEventEnd.isAfter(otherStart)
        ) {
          overlapIndex++;
        }
      }

      const overlapIndent = overlapIndex * 8;

      return {
        useOverlap: true,
        left: `${overlapIndent}px`,
        width: `calc(100% - ${overlapIndent}px)`,
        totalColumns: 2,
      };
    }

    // Use column division for 1-3 concurrent events
    let columnIndex = 0;
    let totalColumns = Math.min(sameStartTime.length, 3);

    if (sameStartTime.length > 1) {
      // Multiple events start at same time - divide into equal sections (max 3)
      columnIndex = sameStartTime.findIndex((e) => e.id === event.id);
      totalColumns = Math.min(sameStartTime.length, 3);
    } else {
      // Single event or different start times - use standard overlap logic
      const eventsStartingBefore = sortedOverlapping.filter((e) =>
        moment(e.startDateTime).isBefore(currentEventStart)
      );

      columnIndex = eventsStartingBefore.length;

      // Calculate max concurrent events at any point
      let maxConcurrent = 1;
      const eventDuration = currentEventEnd.diff(currentEventStart, 'minutes');

      for (let minute = 0; minute <= eventDuration; minute += 15) {
        const checkTime = currentEventStart.clone().add(minute, 'minutes');

        const concurrentAtThisTime = overlappingEvents.filter((otherEvent) => {
          const otherStart = moment(otherEvent.startDateTime);
          const otherEnd = moment(otherEvent.endDateTime);
          return checkTime.isBetween(otherStart, otherEnd, null, '[)');
        });

        maxConcurrent = Math.max(maxConcurrent, concurrentAtThisTime.length);
      }

      totalColumns = Math.min(maxConcurrent, 3);

      // If this creates too many columns, compress
      if (columnIndex >= totalColumns) {
        const compressedIndex = columnIndex - (totalColumns - 1); // 0-based index within compressed slot
        const overlapIndent = compressedIndex * 6;

        return {
          useOverlap: true,
          left: `calc(${
            (100 / totalColumns) * (totalColumns - 1)
          }% + ${overlapIndent}px)`,
          width: `calc(${100 / totalColumns}% - ${overlapIndent}px)`,
          totalColumns,
        };
      }
    }
    return {
      useOverlap: false,
      columnIndex,
      totalColumns,
      width: `${100 / totalColumns}%`,
      left: `${(100 / totalColumns) * columnIndex}%`,
    };
  };

  const layout = calculateEventLayout();

  return (
    <Box
      sx={{
        position: 'absolute',
        top: startPos,
        height: endPos - startPos,
        left: layout.left,
        width: layout.width,

        paddingLeft: '2px',
        paddingRight: '2px',
        zIndex: isSelected ? 10 : 1,
      }}
    >
      <Box
        onClick={(e) => onEventSelect(e, event)}
        onDoubleClick={(e) => onEventEdit(e, event)}
        sx={{
          height: '100%',
          width: '100%',
          backgroundColor: bgColor,
          borderRadius:
            isFirst && isLast
              ? '6px'
              : isFirst
              ? '6px 6px 0 0'
              : isLast
              ? '0 0 6px 6px'
              : '0',
          border: isSelected ? `2px solid ${colors.info}80` : 'none',
          borderLeft: `4px solid ${
            isSelected
              ? colors.info
              : category?.color || event.color || baseColor
          }`,
          cursor: 'pointer',
          display: 'flex',
          padding: '4px 5px',
          minHeight: '20px',
          boxShadow: isSelected
            ? `0 2px 8px ${colors.info}40`
            : '0 1px 3px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isSelected ? colors.info + '50' : baseColor + '60',
            boxShadow: `0 2px 8px ${baseColor}60`,
            transform: 'translateY(-1px)',
          },
        }}
      >
        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            width: '100%',
            overflow: 'hidden', // Ensures content stays within bounds
            position: 'relative', // Required for absolute child (end time)
          }}
        >
          {(selectedPeriod === 'day'
            ? isFirstInWeek || isFirst || !isFirst
            : isFirstInWeek || isFirst) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                alignItems: {
                  xs: 'flex-start',
                  sm: 'center',
                },
                gap: 0.5,
                mb: 0.5,
              }}
            >
              {category?.icon &&
                !layout.useOverlap &&
                layout.totalColumns <= 2 && (
                  <Box
                    component="img"
                    src={category?.icon?.temporaryUrl}
                    sx={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '2px',
                      flexShrink: 0,
                    }}
                  />
                )}

              <Typography
                variant="body2"
                sx={{
                  fontSize: layout.useOverlap
                    ? '11px'
                    : layout.totalColumns > 2
                    ? '11px'
                    : '12px',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                  },
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedPeriod !== 'day' ? (
                  <>
                    {label?.split('').map((char, idx) => (
                      <Box
                        key={idx}
                        component="span"
                        sx={{ display: { xs: 'block', sm: 'inline' } }}
                      >
                        {char}
                      </Box>
                    ))}
                    {label?.length > 5 && (
                      <Box
                        component="span"
                        sx={{ display: { xs: 'block', sm: 'inline' } }}
                      >
                        ...
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: layout.useOverlap
                        ? '11px'
                        : layout.totalColumns > 2
                        ? '11px'
                        : '12px',
                      fontWeight: 500,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </Typography>
                )}
              </Typography>
            </Box>
          )}

          {/* Start time */}
          {isFirst && !layout.useOverlap && layout.totalColumns <= 3 && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '10px',
                opacity: 0.8,
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              <TimeLabel time={startDateTime} format="HH:mm" />
            </Typography>
          )}

          {/* End time positioned bottom right, clipped if overflowing */}
          {isLast && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '2px',
                right: '4px',
                maxWidth: '60px', // optional: prevent long time from spilling
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography
                fontSize={10}
                fontWeight={500}
                textAlign="right"
                sx={{ opacity: 0.6 }}
              >
                <TimeLabel time={endDateTime} format="HH:mm" />
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WeeklyEventBox;
